import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Trash2, AlertTriangle, ChevronRight, Shield, Clock, CheckCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DeleteAccountPage = () => {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post("/account/request-deletion", {
        email,
        reason,
        name
      });
      setSubmitted(true);
      toast.success("Deletion request submitted. Check your email for confirmation.");
    } catch (error) {
      console.error("Deletion request failed:", error);
      toast.error("Failed to submit request. Please try again or email support@recoverymatch.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#1a3a5c] rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">R</span>
            </div>
            <span className="text-xl font-bold">
              <span className="text-[#1a3a5c]">Recovery</span>
              <span className="text-[#e9a84b]">Match</span>
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Delete Your Account</h1>
          <p className="text-gray-600">We're sorry to see you go. Here's how to delete your RecoveryMatch account.</p>
        </div>

        {/* Warning Card */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
          <div className="flex gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-2">Before you delete</h3>
              <p className="text-amber-700 text-sm">
                Account deletion is <strong>permanent</strong>. Once deleted, you will lose access to:
              </p>
              <ul className="mt-2 space-y-1 text-amber-700 text-sm">
                <li>• All your matches and conversations</li>
                <li>• Your profile, photos, and bio</li>
                <li>• Any remaining coins or active subscriptions</li>
                <li>• Your recovery milestones and history</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Options Section */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Option 1: In-App */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete from the App</h3>
            <p className="text-gray-600 text-sm mb-4">The quickest way to delete your account:</p>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium">1</span>
                <span>Open RecoveryMatch app</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium">2</span>
                <span>Go to <strong>Profile → Settings</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium">3</span>
                <span>Scroll to <strong>Account → Delete Account</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium">4</span>
                <span>Confirm deletion</span>
              </li>
            </ol>
          </div>

          {/* Option 2: Email Request */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Request via Email</h3>
            <p className="text-gray-600 text-sm mb-4">Can't access the app? Email us directly:</p>
            <a 
              href="mailto:support@recoverymatch.com?subject=Account%20Deletion%20Request"
              className="inline-flex items-center gap-2 text-[#1a3a5c] font-medium hover:underline"
            >
              support@recoverymatch.com
              <ChevronRight className="w-4 h-4" />
            </a>
            <p className="text-gray-500 text-xs mt-4">
              Include the email address associated with your account.
            </p>
          </div>
        </div>

        {/* Request Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-12">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Submit Deletion Request</h3>
          
          {submitted ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Request Submitted</h4>
              <p className="text-gray-600">
                We've sent a confirmation email to <strong>{email}</strong>.<br />
                Please check your inbox (and spam folder).
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your name (optional)
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3a5c] focus:border-transparent"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address associated with your account *
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3a5c] focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for leaving (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a3a5c] focus:border-transparent"
                  placeholder="Help us improve by sharing why you're leaving..."
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Deletion Request"
                )}
              </button>
            </form>
          )}
        </div>

        {/* Timeline */}
        <div className="bg-gray-100 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">What happens next?</h3>
          </div>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>• We'll verify your identity via the email provided</li>
            <li>• Your account will be deactivated immediately</li>
            <li>• All data will be permanently deleted within <strong>30 days</strong></li>
            <li>• You'll receive a confirmation email once complete</li>
          </ul>
        </div>

        {/* Footer Links */}
        <div className="text-center mt-12 text-sm text-gray-500">
          <p>
            Questions? Contact us at{" "}
            <a href="mailto:support@recoverymatch.com" className="text-[#1a3a5c] hover:underline">
              support@recoverymatch.com
            </a>
          </p>
          <div className="mt-4 flex justify-center gap-4">
            <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:underline">Terms of Service</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DeleteAccountPage;
